import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock, Eye, EyeOff, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword, formatApiError } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!token) { setError(t("auth.resetMissingToken")); return; }
    if (password.length < 6) { setError(t("auth.resetTooShort")); return; }
    if (password !== confirm) { setError(t("auth.resetMismatch")); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      navigate("/login?reset=success", { replace: true });
    } catch (e) {
      const detail = formatApiError ? formatApiError(e.response?.data?.detail) : null;
      // Backend returns a generic message for bad/expired tokens.
      setError(detail || t("auth.resetInvalidToken"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-md px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-slate-900 flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-base font-semibold tracking-tight text-slate-900">Chronicles</span>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-md px-5 py-10" data-testid="reset-password-page">
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-3">{t("auth.appTagline")}</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{t("auth.resetTitle")}</h1>
        <p className="mt-2 text-sm text-slate-600">{t("auth.resetSubtitle")}</p>

        {!token ? (
          <p className="mt-6 text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2" data-testid="reset-missing-token">
            {t("auth.resetMissingToken")}
          </p>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <div className="space-y-1.5">
              <Label>{t("auth.resetNewPassword")}</Label>
              <div className="relative">
                <Input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl h-12 pr-11"
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label={show ? "Hide password" : "Show password"}
                  data-testid="reset-toggle-visibility"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("auth.resetConfirmPassword")}</Label>
              <Input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="rounded-xl h-12"
                data-testid="reset-confirm-password"
              />
            </div>

            {error ? <p className="text-sm text-rose-600" data-testid="reset-error">{error}</p> : null}

            <Button
              type="submit"
              className="rounded-full h-12 w-full bg-slate-900 hover:bg-slate-800"
              disabled={busy}
              data-testid="reset-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("auth.resetSubmit")}
            </Button>
          </form>
        )}

        <button
          className="mt-6 text-sm text-slate-500 hover:text-slate-900 underline"
          onClick={() => navigate("/login")}
          data-testid="reset-back-to-login"
        >
          {t("auth.backToLogin")}
        </button>
      </main>
    </div>
  );
}
